import { StandardEvents } from "@shopify/events";

// Shopify's section response supplies localized variant prices; never parse formatted money.
class SupportBundles extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    this.scope = this.closest(".shopify-section, dialog");
    const options = { signal: this.controller.signal };
    this.addEventListener("change", this.choose, options);
    this.scope?.addEventListener("change", this.sync, options);
    this.scope?.addEventListener("input", this.sync, options);
    this.scope?.addEventListener(
      "quantity-selector:update",
      this.sync,
      options,
    );
    this.scope?.addEventListener(
      StandardEvents.productSelect,
      this.selectVariant,
      options,
    );
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => !this.contains(record.target))) this.sync();
    });
    if (this.scope)
      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          "min",
          "max",
          "step",
          "disabled",
          "data-cart-quantity",
        ],
      });
    this.sync();
  }

  quantity() {
    return this.scope?.querySelector(
      'product-form-component input[name="quantity"]',
    );
  }

  allowed(count, input) {
    if (
      !input ||
      input.disabled ||
      this.pending ||
      this.dataset.available !== "true"
    )
      return false;
    const min = Number(input.min || 1);
    const step = Number(input.step) || 1;
    const max =
      input.max === ""
        ? Infinity
        : Number(input.max) - Number(input.dataset.cartQuantity || 0);
    return count >= min && count <= max && (count - min) % step === 0;
  }

  sync = (event) => {
    const quantity = this.quantity();
    // Direct edits to the quantity input (typing, +/- buttons) should hand
    // control back to the quantity-driven sync model.
    if (
      event?.target instanceof HTMLInputElement &&
      event.target === quantity &&
      this.userChose !== undefined &&
      Number(event.target.value) !== this.userChose
    ) {
      this.userChose = undefined;
    }
    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      // While a variant switch is in flight (`selectVariant` set `pending`),
      // freeze every pair's current checked/disabled state so the user keeps
      // a responsive UI instead of watching the radios uncheck and lock up
      // for the duration of the Section Rendering round trip. The
      // replacement HTML arriving in `selectVariant` will re-establish state.
      if (this.pending) return;
      const allowed = this.allowed(Number(radio.value), quantity);
      radio.disabled = !allowed;
      // `userChose` freezes the visual selection during the brief window
      // between the user's click and the quantity selector echoing the new
      // value back. Every radio is forced to its own `checked` state instead
      // of being rewritten from `quantity.value`, which guarantees that the
      // previous pair loses its tick the instant a different pair is picked.
      if (this.userChose !== undefined) {
        radio.checked = radio.value === this.userChose;
        return;
      }
      radio.checked =
        allowed && Number(radio.value) === Number(quantity?.value);
    });
  };

  choose = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== "radio") return;
    // Pin the selection immediately so the tick is rendered before any
    // follow-up sync/observer tick can clear it. `userChose` stays set so
    // every subsequent sync (including the one triggered by our own change
    // dispatch) keeps this radio checked and the previous one unchecked,
    // even if the underlying quantity input is momentarily invalid.
    this.userChose = radio.value;
    this.querySelectorAll('input[type="radio"]').forEach((candidate) => {
      candidate.checked = candidate === radio;
    });
    const input = this.quantity();
    if (!input) return;
    input.value = radio.value;
    const selector = input.closest("quantity-selector-component");
    if (typeof selector?.setQuantity === "function") {
      selector.setQuantity({ target: input, preventDefault() {} });
    } else {
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.sync();
  };

  selectVariant = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest("product-card") ||
      !event.promise
    )
      return;
    const sequence = (this.sequence = (this.sequence || 0) + 1);
    this.pending = true;
    this.setAttribute("aria-busy", "true");
    this.sync();
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || sequence !== this.sequence) return;
        if (
          !detail?.html ||
          (!detail.newProduct &&
            detail.productId &&
            String(detail.productId) !== this.dataset.productId)
        ) {
          this.pending = false;
          this.removeAttribute("aria-busy");
          this.sync();
          return;
        }
        const replacement = [
          ...detail.html.querySelectorAll("support-bundles"),
        ].find((element) => element.dataset.blockId === this.dataset.blockId);
        // Never keep another variant's price when the new response omits this block.
        this.hidden = !replacement;
        if (replacement) {
          const focusedValue = this.contains(document.activeElement)
            ? document.activeElement.value
            : null;
          this.innerHTML = replacement.innerHTML;
          this.dataset.productId = replacement.dataset.productId;
          this.dataset.available = detail.resource
            ? replacement.dataset.available
            : "false";
          this.pending = false;
          this.removeAttribute("aria-busy");
          // The replacement radio list may not contain the previously chosen
          // pair value (e.g. the new variant caps quantity at 3). Clear
          // `userChose` so the post-swap sync doesn't pin a stale tick on a
          // radio that no longer exists.
          if (
            this.userChose !== undefined &&
            ![...this.querySelectorAll('input[type="radio"]')].some(
              (radio) => radio.value === this.userChose
            )
          ) {
            this.userChose = undefined;
          }
          this.sync();
          if (focusedValue)
            [...this.querySelectorAll('input[type="radio"]')]
              .find((radio) => radio.value === focusedValue && !radio.disabled)
              ?.focus({ preventScroll: true });
        }
      })
      .catch(() => {
        if (!this.isConnected || sequence !== this.sequence) return;
        // The native picker handles errors; hide stale totals until a successful selection.
        this.hidden = true;
        this.pending = false;
        this.removeAttribute("aria-busy");
      });
  };

  disconnectedCallback() {
    this.sequence = (this.sequence || 0) + 1;
    this.controller?.abort();
    this.observer?.disconnect();
  }
}

if (!customElements.get("support-bundles"))
  customElements.define("support-bundles", SupportBundles);
